/**
 * 高德 JS API 2.0 共享加载器（core 单例 + 插件按需）
 *
 * 迁移兼容：仅 services/map/providers/amap.js 消费（后端显式 MAP_PROVIDER=amap 时才可能加载）。
 * 配置（jsKey + serviceHost）由调用方从 /map/config 获取后传入；
 *   securityCode 永不进前端（后端 serviceHost 代理附加）。
 *
 * 加载策略：
 *   - core（AMap.Map 等）模块级单例 promise，全应用只注一次 <script>
 *   - 插件（AutoComplete/PlaceSearch/Geocoder...）AMap.plugin 按需加载 + Set 去重
 *
 * 坐标系：GCJ-02（高德原生）。浏览器 GPS（WGS84）须由调用方 AMap.convertFrom 转换。
 */

// core 单例 promise：首次加载注入 script，后续复用（禁止重复注 script）
let corePromise = null
// 已加载插件集合：AMap.plugin 按需加载后登记，重复请求直接跳过
const loadedPlugins = new Set()

function loadCore (jsKey) {
  // 已就绪（含热重载后 window.AMap 残留）直接返回
  if (window.AMap && window.AMap.Map) return Promise.resolve(window.AMap)
  if (corePromise) return corePromise
  corePromise = new Promise((resolve, reject) => {
    const cb = '__amap_init__'
    window[cb] = function () { resolve(window.AMap); try { delete window[cb] } catch (e) {} }
    const s = document.createElement('script')
    s.src = 'https://webapi.amap.com/maps?v=2.0&key=' + encodeURIComponent(jsKey) + '&callback=' + cb
    s.onerror = function () { corePromise = null; reject(new Error('amap_load_fail')) }
    document.head.appendChild(s)
    // 8s 超时兜底（弱网/被墙）：释放单例允许下次重试
    setTimeout(function () {
      if (!window.AMap) { corePromise = null; reject(new Error('amap_load_timeout')) }
    }, 8000)
  })
  return corePromise
}

/**
 * 加载 AMap core + 指定插件
 * @param {Object} opts
 *   { jsKey, securityConfig?, plugins?: string[] }
 *   plugins 形如 ['AMap.AutoComplete','AMap.PlaceSearch','AMap.Geocoder']
 * @returns {Promise<AMap>}
 */
function loadAmap (opts) {
  const { jsKey, securityConfig, plugins } = opts || {}
  if (securityConfig) {
    // JSAPI 2.0 平台校验只认绝对 URL：相对路径 serviceHost 会被其 split("/")[0] 算成空串，
    // localhost 访问时触发原生 alert（阻塞整页）。此处统一拼成绝对 URL，一处转换。
    const cfg = Object.assign({}, securityConfig)
    if (cfg.serviceHost && !/^https?:\/\//.test(cfg.serviceHost)) {
      cfg.serviceHost = window.location.protocol + '//' + window.location.host + cfg.serviceHost
    }
    window._AMapSecurityConfig = cfg
  }
  return loadCore(jsKey).then((AMap) => {
    const need = (plugins || []).filter((p) => !loadedPlugins.has(p))
    if (!need.length) return AMap
    return new Promise((resolve, reject) => {
      AMap.plugin(need, () => {
        need.forEach((p) => loadedPlugins.add(p))
        resolve(AMap)
      })
    })
  })
}

export { loadAmap }
